/**
 * Pressed state for buttons: a wash grows from the press point (the center for Enter or Space)
 * until it covers the button, and fades out after release. Styles live in `base.css`
 * (`.button-ripple`); the colors come from `--button-ripple-color`.
 */

const BUTTON_SELECTOR = '.button, .button-secondary, .button-custom, button.shopify-payment-button__button--unbranded';
const GROW_DURATION = 400;
const FADE_DURATION = 200;

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * @param {Element} button
 * @returns {boolean}
 */
const isDisabled = (button) => button.matches(':disabled, [aria-disabled="true"]');

/** Internal controls that restyle the button class keep their own feedback. */
const INTERNAL_SELECTOR =
  '.quick-add__button, .email-signup__button, .header-actions__action, .close-button, .menu-drawer__back-button, .cart-items__remove, .variant-item__remove, .predictive-search__close-modal-button, .predictive-search__search-button, .skip-to-content-link, .button-unstyled';

/**
 * @param {Element} button
 * @returns {boolean}
 */
const isIgnored = (button) => isDisabled(button) || button.matches(INTERNAL_SELECTOR);

/**
 * Starts a ripple at a point inside the button.
 *
 * @param {HTMLElement} button
 * @param {number} x - Distance from the button's left edge
 * @param {number} y - Distance from the button's top edge
 * @returns {() => void} Call to let the ripple fade out
 */
function startRipple(button, x, y) {
  const { width, height } = button.getBoundingClientRect();
  const radius = Math.hypot(Math.max(x, width - x), Math.max(y, height - y));

  const ripple = document.createElement('span');
  ripple.className = 'button-ripple';
  ripple.setAttribute('aria-hidden', 'true');
  ripple.style.width = `${radius * 2}px`;
  ripple.style.height = `${radius * 2}px`;
  ripple.style.left = `${x - radius}px`;
  ripple.style.top = `${y - radius}px`;
  button.append(ripple);

  // With reduced motion the wash appears at full size right away
  const grow = reduceMotion.matches
    ? null
    : ripple.animate([{ transform: 'scale(0)' }, { transform: 'scale(1)' }], {
        duration: GROW_DURATION,
        easing: 'ease-out',
        fill: 'forwards',
      });

  if (!grow) ripple.style.transform = 'scale(1)';

  let released = false;

  return async () => {
    if (released) return;
    released = true;

    await grow?.finished.catch(() => {});

    const fade = ripple.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: reduceMotion.matches ? 0 : FADE_DURATION,
      fill: 'forwards',
    });

    await fade.finished.catch(() => {});
    ripple.remove();
  };
}

/**
 * Releases the ripple when the press ends.
 * @param {() => void} release
 * @param {string[]} events
 * @param {EventTarget} target
 */
function releaseOn(release, events, target) {
  const controller = new AbortController();

  const done = () => {
    controller.abort();
    release();
  };

  for (const name of events) target.addEventListener(name, done, { signal: controller.signal });
}

document.addEventListener(
  'pointerdown',
  (event) => {
    if (event.button !== 0 || !(event.target instanceof Element)) return;

    const button = event.target.closest(BUTTON_SELECTOR);

    if (!(button instanceof HTMLElement) || isIgnored(button)) return;

    const rect = button.getBoundingClientRect();
    const release = startRipple(button, event.clientX - rect.left, event.clientY - rect.top);

    releaseOn(release, ['pointerup', 'pointercancel'], window);
  },
  { passive: true }
);

document.addEventListener('keydown', (event) => {
  if (event.repeat || (event.key !== 'Enter' && event.key !== ' ')) return;
  if (!(event.target instanceof Element)) return;

  const button = event.target.closest(BUTTON_SELECTOR);

  if (!(button instanceof HTMLElement) || isIgnored(button)) return;

  // Links only activate with Enter
  if (event.key === ' ' && button.tagName === 'A') return;

  const rect = button.getBoundingClientRect();
  const release = startRipple(button, rect.width / 2, rect.height / 2);

  releaseOn(release, ['keyup', 'blur'], button);
});
